import { faClose } from "@fortawesome/pro-regular-svg-icons";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import { Button, Stack } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useRef } from "react";
import { useEventCallback } from "usehooks-ts";

import { Icon, IconButton, Tooltip, Typography } from "@/components/ui-v2";
import { useBasicData, useTrackNextAppSwitch } from "@/hooks";
import { getWebAppLoginUrl } from "@/utils/env";
import { capturePostHogEvent } from "@/utils/posthog";

import {
  NEW_LAYER_BANNER_SNOOZE_DAYS,
  useNewLayerBannerSnooze,
} from "./useNewLayerBannerSnooze";

/** Fixed banner height (px). The app shell offsets the nav Drawer by this. */
export const NEW_LAYER_BANNER_HEIGHT = 44;

const Root = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(4),
  height: NEW_LAYER_BANNER_HEIGHT,
  flexShrink: 0,
  padding: theme.spacing(0, 4),
  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, #7B2FF7 50%, ${theme.palette.primary.main} 100%)`,
  color: theme.palette.common.white,
}));

/** Single line of copy; when room runs out the tail is cut with an ellipsis. */
const Copy = styled(Typography)({
  minWidth: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const snoozeLabel = `Remind me in ${NEW_LAYER_BANNER_SNOOZE_DAYS} days`;

export const NewLayerBanner = () => {
  const { isSnoozed, snooze } = useNewLayerBannerSnooze();
  const { user } = useBasicData();
  const trackNextAppSwitch = useTrackNextAppSwitch();

  const userId = user?.id;
  const hasTrackedShown = useRef(false);
  useEffect(() => {
    if (isSnoozed || !userId || hasTrackedShown.current) return;
    hasTrackedShown.current = true;
    capturePostHogEvent("next_app.banner_shown", userId);
  }, [isSnoozed, userId]);

  const onSnooze = useEventCallback(() => {
    if (userId) {
      capturePostHogEvent("next_app.banner_dismissed", userId, {
        snoozeDays: String(NEW_LAYER_BANNER_SNOOZE_DAYS),
      });
    }
    snooze();
  });

  const onOpenNextApp = useEventCallback(() => {
    trackNextAppSwitch("banner");
    window.location.href = getWebAppLoginUrl();
  });

  if (isSnoozed) return null;

  return (
    <Root>
      <Stack
        direction="row"
        sx={{ alignItems: "center", gap: 4, flex: 1, minWidth: 0 }}
      >
        <Copy variant="small" sx={{ color: "common.white" }}>
          <Typography component="span" variant="small" sx={{ fontWeight: 700 }}>
            The new Layer is ready.
          </Typography>
          <Typography
            component="span"
            variant="small"
            sx={{ display: { xs: "none", md: "inline" } }}
          >
            {" "}
            Same workspace, same assets.
          </Typography>
          <Typography
            component="span"
            variant="small"
            sx={{ display: { xs: "none", lg: "inline" } }}
          >
            {" "}
            Now with a chat-centric experience, shared sessions, a session
            canvas, workflows, the latest models and more.
          </Typography>
        </Copy>
        <Button
          onClick={onOpenNextApp}
          variant="contained"
          color="secondary"
          size="medium"
          rounded
          endIcon={<Icon icon={faArrowRight} />}
          sx={{ flexShrink: 0 }}
        >
          Open the new Layer
        </Button>
      </Stack>
      <Tooltip title={snoozeLabel} placement="bottom">
        <IconButton
          aria-label={snoozeLabel}
          variant="quaternary"
          onClick={onSnooze}
          sx={{ color: "common.white", flexShrink: 0 }}
        >
          <Icon icon={faClose} />
        </IconButton>
      </Tooltip>
    </Root>
  );
};
